import { GenericAbortSignal } from 'axios';

import { Paginated } from 'models/flexible/Paginated';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

// The registry's collected-field configuration (OLIO-3074, admin → Portal Systems):
// the full collectable universe with each field's configured state, per system. The
// end-user registry payload (services/api/portalSystems) carries only the effective
// selection; this admin payload carries every row so the UI can render the disabled
// ones and lock the non-negotiables.
export interface AdminRetrievalField {
  name: string;
  label: string;
  type: string;
  cardinality?: 'many';
  required: boolean;
  enabled: boolean;
}

export interface AdminScanField {
  name: string;
  label: string;
  type: string;
  required: boolean;
  enabled: boolean;
  showOnSweepsTable: boolean;
}

export interface AdminPortalSystem {
  id: string;
  systemName: string;
  displayName: string;
  // The portal's own front door (OLIO-3131): where a first sweep recording starts and the
  // login anchor a credential save is verified against when nothing is recorded. https only.
  homeUrl: string | null;
  primaryId: string;
  primaryIdOptions: string[];
  urlIdentifiesReferral: boolean;
  urlRegex: string | null;
  // The list-page markers (OLIO-3492): the portal's opt-in, and — field mode only — what an
  // identifier looks like in this portal's text, which is what the scan matches against there.
  worklistMarkers: boolean;
  identifierRegex: string | null;
  retrievalFields: AdminRetrievalField[];
  scanFields: AdminScanField[];
  // Unresolved problem reports from the extension; on index rows only.
  openProblemReports?: number;
}

// camelCase in — OliveBranch snake_cases on the wire. Omitted keys keep their stored
// selection; only send what the admin moved.
export interface UpdatePortalSystemInput {
  displayName?: string;
  retrievalFields?: string[];
  scanFields?: string[];
  sweepTableFields?: string[];
  primaryId?: string;
  urlIdentifiesReferral?: boolean;
  urlRegex?: string | null;
  // Required and https-only; the api rejects anything else.
  homeUrl?: string;
  worklistMarkers?: boolean;
  identifierRegex?: string | null;
}

// Adding a portal is an INSERT (OLIO-3195): the names make a fully usable registry row —
// field config stays at the defaults until edited. `systemName` is the identifier on the
// wire and is settable only here, never on update.
export interface CreatePortalSystemInput {
  systemName: string;
  displayName: string;
  homeUrl: string;
  urlRegex: string;
}

export const adminPortalSystemQueryKeys = {
  index: ['adminPortalSystems'] as const,
  show: (id: string) => ['adminPortalSystems', id] as const,
};

const URL = 'admin/portal_systems';

export async function indexAdminPortalSystems(
  params: BaseIndexQueryParams,
  signal?: GenericAbortSignal
): Promise<Paginated<AdminPortalSystem>> {
  return http.get(URL, { params, signal }).then((res) => res.data);
}

export async function showAdminPortalSystem(id: string, signal?: GenericAbortSignal) {
  return http.get<AdminPortalSystem>(`${URL}/${id}`, { signal }).then((res) => res.data);
}

export async function createAdminPortalSystem(input: CreatePortalSystemInput) {
  return http.post<AdminPortalSystem>(URL, input).then((res) => res.data);
}

export async function updateAdminPortalSystem(id: string, input: UpdatePortalSystemInput) {
  return http.patch<AdminPortalSystem>(`${URL}/${id}`, input).then((res) => res.data);
}
